import * as React from 'react';
import { NavbarDropdownMenuFieldsFragment } from 'types/generated';
import { motion } from 'framer-motion';
import { getOr } from '@/utils/options';
import clsx from 'clsx';
import { Label } from 'components/src/typography';
import LinkCategory from './LinkCategory';
import { ArrowLeftIcon } from 'components/src/svg';
import MediaBoxWithFade from './MediaWithFade';
import { bezierEase } from '@/utils/utils';

type Props = {
  data: NavbarDropdownMenuFieldsFragment;
  openDropdownTitle: string | null | undefined;
  showMobileMenu: boolean;
  setOpenDropdownTitle: (title: string | null) => void;
};

const NavbarDropdownMenu = ({ data, openDropdownTitle, showMobileMenu, setOpenDropdownTitle }: Props) => {
  const [contentHeight, setContentHeight] = React.useState<number | undefined>(undefined);

  const showDropdownForLink = Boolean(data.title && openDropdownTitle === data.title);
  const MotionLabel = motion.create(Label);
  React.useEffect(() => {
    setContentHeight(document.getElementById('submenu')?.clientHeight);
  }, [showDropdownForLink, data]);

  const handleBackClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setOpenDropdownTitle(null);
  };

  return (
    <motion.div
      layoutId="navbardropdown"
      initial={showMobileMenu ? { height: 'auto' } : { height: 0, padding: 0, marginTop: 0 }}
      animate={
        showMobileMenu
          ? { height: contentHeight, transition: { ease: bezierEase, duration: 0.24 } }
          : { height: contentHeight, marginTop: '4px', transition: { ease: bezierEase, duration: 0.24 } }
      }
      exit={showMobileMenu ? { height: 'auto' } : { height: 0, padding: 0, marginTop: 0 }}
      className="flex flex-col items-start gap-lg self-stretch text-white-75"
    >
      {showDropdownForLink && (
        <div
          id="submenu"
          aria-label="submenu"
          className={clsx('flex items-start self-stretch rounded-16', {
            'top-0 h-full max-h-[80vh] flex-col gap-sm overflow-hidden': showMobileMenu,
            'gap-lg': !showMobileMenu,
          })}
        >
          {showMobileMenu && (
            <div className="flex flex-row flex-wrap content-center items-center gap-xs self-stretch py-4">
              <button className="px-md flex flex-1 gap-sm py-[10px]" onClick={(e) => handleBackClick(e)}>
                <ArrowLeftIcon />
                <MotionLabel whileHover={{ textDecoration: 'underline' }} classes="label-lg text-red-100">
                  {getOr(data.title, '')}
                </MotionLabel>
              </button>
            </div>
          )}
          <motion.div
            layoutId="submenu-content"
            id="submenu-content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { ease: bezierEase, duration: 0.24 } }}
            exit={{ opacity: 0, transition: { ease: bezierEase, duration: 0.12 } }}
            className={clsx('gap-lg rounded-16 bg-red-100', {
              'flex flex-1 flex-col items-start p-xl sm:flex-row': !showMobileMenu,
              'no-scrollbar flex h-full max-h-[80vh] w-full flex-col items-center overflow-y-auto p-xl': showMobileMenu,
            })}
          >
            {data.linkCategoriesCollection?.items?.map((linkCategory, index) => (
              <motion.div
                layoutId={'linkCategories-' + index}
                key={`linkCategory-${index}`}
                onClick={() => setOpenDropdownTitle(null)}
                className={clsx({
                  'items-start self-stretch': showMobileMenu,
                  'w-[60%]': !showMobileMenu && index === 0,
                })}
              >
                <LinkCategory data={linkCategory} idx={index} showMobileMenu={showMobileMenu} />
              </motion.div>
            ))}
          </motion.div>
          {!showMobileMenu && data.adImage && (
            <motion.div
              layoutId="mediabox"
              style={{ maxHeight: `${contentHeight}px` }}
              className={`hidden h-full self-stretch overflow-hidden rounded-16 md:block`}
            >
              <MediaBoxWithFade data={data.adImage} />
            </motion.div>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default NavbarDropdownMenu;
